import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, withLatestFrom } from 'rxjs/operators';

import {
  CpScopeOfWork,
  LoadAvailableCpTags,
  LoadAvailableMcpTags,
  LoadAvailableTagsSuccess,
  LoadTagProjections,
  LoadTagProjectionsSuccess,
  McpScopeOfWork,
  SearchTags,
  SearchTagsSuccess,
  SetSerialNumberCp,
  SetSerialNumberMcp,
  TagActionTypes,
} from '@completion/actions';
import { State } from '@completion/reducers';
import { getCurrentCpId, getCurrentMcpId, getCurrentProject } from '@completion/selectors';
import { TagService } from '@completion/services';
import { NotificationService } from '../../services/notification.service';
import { Project, Tag, CheckSheetAssignment, TagProjection } from '@completion/models';
import { trackResource } from './resource-fetch.util';

@Injectable()
export class TagEffect {
  constructor(
    private readonly actions$: Actions,
    private readonly service: TagService,
    private readonly store: Store<State>,
    private readonly toastr: NotificationService,
  ) {}

  loadAvailableMcpTags$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadAvailableMcpTags>(TagActionTypes.LoadAvailableMcpTags),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentMcpId)),
      filter(([_, project, mcpId]) => project != null && mcpId != null),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.service,
        method: 'getMcpAvailableTags',
        serviceArgs: ([_, project, mcpId]) => [project.id, mcpId],
        onSuccess: (tags: Tag[]) => new LoadAvailableTagsSuccess(tags),
      }),
    ),
  );

  loadAvailableCpTags$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadAvailableCpTags>(TagActionTypes.LoadAvailableCpTags),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId)),
      filter(([_, project, cpId]) => project != null && cpId != null),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.service,
        method: 'getCpAvailableTags',
        serviceArgs: ([_, project, cpId]) => [project.id, cpId],
        onSuccess: (tags: Tag[]) => new LoadAvailableTagsSuccess(tags),
      }),
    ),
  );

  setSerialNumberTagCp$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SetSerialNumberCp>(TagActionTypes.SetSerialNumberCp),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.service,
        method: 'setSerialNumberTag',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.tag],
        onSuccess: () => new CpScopeOfWork(),
      }),
    ),
  );

  setSerialNumberTagMcp$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SetSerialNumberMcp>(TagActionTypes.SetSerialNumberMcp),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.service,
        method: 'setSerialNumberTag',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.tag],
        onSuccess: () => new McpScopeOfWork(),
      }),
    ),
  );

  searchTags$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SearchTags>(TagActionTypes.SearchTags),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.service,
        method: 'searchTags',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.search],
        onSuccess: (tags: CheckSheetAssignment[]) => new SearchTagsSuccess(tags),
      }),
    ),
  );

  loadTagProjections$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadTagProjections>(TagActionTypes.LoadTagProjections),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.service,
        method: 'loadTagProjections',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (projections: TagProjection[]) => new LoadTagProjectionsSuccess(projections),
      }),
    ),
  );
}
